import { addressNormalize } from '@zerodep/address-normalize';

export function compareIntelligentActionResults(
  extracted: any,
  actual: any,
  comparison?: string
) {
  if (!extracted && actual) {
    return false;
  }

  if (extracted && !actual) {
    return false;
  }

  switch (comparison) {
    case 'serialNumberOverlap': {
      const reference = new Set(parseSerialNumberList(actual).map(serial => serial.toUpperCase()));
      return parseSerialNumberList(extracted).some(serial => reference.has(serial.toUpperCase()));
    }
    case 'lessThan':
      return compareLessThan(extracted, actual);
    case 'greaterThan':
      return compareGreaterThan(extracted, actual);
    case 'lessThanEqual':
      return compareLessThanEqual(extracted, actual);
    case 'greaterThanEqual':
      return compareGreaterThanEqual(extracted, actual);
    case 'inRange': {
      if ((extracted as string).includes('-')) {
        return matchExtractedByRange(extracted, actual, '-');
      } else if ((extracted as string).includes('~')) {
        return matchExtractedByRange(extracted, actual, '~');
      }
    }
    case 'manufacturer':
      return matchNormalizedManufacturer(extracted, actual);
    case 'batteryNameplateModelNumber':
      return matchBatteryNameplateModelNumber(extracted, actual);
    case 'wildCardModelNumber':
      return matchWildCardModelNumber(extracted, actual);
    case 'numericEquality':
      return basicNumericEqualityMatch(extracted, actual);
    case 'address':
      return matchNormalizedAddressComEd(extracted, actual);
  }

  return compareValuesStandard(extracted, actual);
}

export function basicNumericEqualityMatch(
  extracted: any,
  actual: any
): boolean {
  var extractedNormalized = String(extracted).trim().toLowerCase();
  var actualNormalized = String(actual).trim().toLowerCase();

  if (!extractedNormalized || !actualNormalized) {
    return extractedNormalized === actualNormalized;
  }

  const extractedNumber = Number(extractedNormalized);
  const actualNumber = Number(actualNormalized);

  return (
    Number.isFinite(extractedNumber) &&
    Number.isFinite(actualNumber) &&
    extractedNumber === actualNumber
  );
}

export function computeDynamicResultMessage(
  extractedValue: any,
  actualValue: any,
  notLoaded: boolean,
  comparison,
  isEmptyOnLoad: boolean
) {
  const notExtractedMessage: string = 'Could not extract value from file.';
  const notLoadedMessage: string = 'No data found, please wait then refresh';
  const correctExtractionMessage: string =
    'The extracted and form values match.';
  const incorrectExtractionMessage: string =
    'Form value does not match extracted value.';

  if (notLoaded) {
    return notLoadedMessage;
  }

  if (comparison === 'serialNumberOverlap') {
    if (!parseSerialNumberList(extractedValue).length || !parseSerialNumberList(actualValue).length) {
      return $localize`Could not extract serial numbers from one or both files.`;
    }
    return compareIntelligentActionResults(extractedValue, actualValue, comparison)
      ? $localize`At least one nameplate serial number matches the serial-number list.`
      : $localize`No nameplate serial numbers match the serial-number list.`;
  }

  if (extractedValue === '' && actualValue === '') {
    return notExtractedMessage;
  }

  if (
    compareIntelligentActionResults(extractedValue, actualValue, comparison)
  ) {
    return correctExtractionMessage;
  } else {
    if (!extractedValue && isEmptyOnLoad) {
      return notExtractedMessage;
    } else {
      return incorrectExtractionMessage;
    }
  }
}

export function parseSerialNumberList(value: unknown): string[] {
  let serials: unknown = value;
  if (typeof value === 'string') {
    const text = value.trim();
    if (!text) return [];
    if (text.startsWith('[') || text.startsWith('{')) {
      try {
        serials = JSON.parse(text);
      } catch {
        return [];
      }
    } else {
      serials = text.split(/\r?\n/);
    }
  }
  if (!Array.isArray(serials) || !serials.every(serial => typeof serial === 'string')) return [];
  return serials.map(serial => serial.trim()).filter(Boolean);
}

function compareValuesStandard(extracted: any, actual: any) {
  if (actual === extracted) {
    return true;
  }

  const extractedLower = extracted.toLowerCase();
  const actualLower = actual.toLowerCase();

  if (actualLower === extractedLower) {
    return true;
  }

  if (
    actualLower.includes(extractedLower) ||
    extractedLower.includes(actualLower)
  ) {
    return true;
  }

  if (actualLower.trim() === extractedLower.trim()) {
    return true;
  }

  var extractedNormalized = extractedLower
    .replace('-', '')
    .replace('.', '')
    .replace(',', '')
    .replace(' ', '')
    .trim();
  var actualNormalized = actualLower
    .replace('-', '')
    .replace('.', '')
    .replace(',', '')
    .replace(' ', '')
    .trim();

  if (extractedNormalized === actualNormalized) {
    return true;
  }

  return false;
}

// replace any characters we consider wildcards with the actual wildcard character
// convert extracted value to a regular expression and apply it to the model, if it's contained in it or we get a match consider it good
function matchWildCardModelNumber(extracted: any, actual: any): boolean {
  // Actual value without extra content in {}
  const cleanActual = String(actual)
    .replace(/\{.*?\}/g, '')
    .trim();

  var wildCardMatch: boolean = false;
  var extractedInActual: boolean = false;

  if (cleanActual.length > 0) {
    const escaped = String(actual).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern = escaped.replace(/X/g, '.').replace(/Y/g, '.');
    wildCardMatch = new RegExp(pattern, 'i').test(extracted);

    extractedInActual = isExtractedModelInActual(extracted, cleanActual);
  }

  var standardMatch = compareValuesStandard(extracted, actual);

  return wildCardMatch || standardMatch || extractedInActual;
}

function matchBatteryNameplateModelNumber(
  extracted: unknown,
  actual: unknown
): boolean {
  const extractedValue = String(extracted ?? '').trim();
  const actualValue = String(actual ?? '').trim();

  if (!extractedValue || !actualValue) {
    return false;
  }

  const cleanExtracted = stripTrailingModelMetadata(extractedValue);
  const cleanActual = stripTrailingModelMetadata(actualValue);

  if (!cleanExtracted || !cleanActual) {
    return false;
  }

  const normalizedExtracted = normalizeBatteryModelNumber(cleanExtracted);
  const normalizedActual = normalizeBatteryModelNumber(cleanActual);

  if (!normalizedExtracted || !normalizedActual) {
    return false;
  }

  if (normalizedExtracted.toLowerCase() === normalizedActual.toLowerCase()) {
    return true;
  }

  return matchesBatteryModelTemplate(normalizedExtracted, normalizedActual);
}

function stripTrailingModelMetadata(value: string): string {
  // Matches a balanced terminal {...} group, including surrounding whitespace, without consuming nested braces.
  // Example: the regex matches " {10kW}" in "1707000-XX-Y {10kW}", leaving the non-regex model "1707000-XX-Y".
  const trailingCurlyMetadataPattern = /\s*\{[^{}]*\}\s*$/;
  // Matches a balanced terminal [...] group and captures its contents, while rejecting nested square brackets.
  // Example: the regex matches " [11.5kW]" in "1707000-XX-Y [11.5kW]", leaving the non-regex model "1707000-XX-Y".
  const trailingSquareMetadataPattern = /\s*\[([^[\]]*)\]\s*$/;
  let cleanedValue = value.trim();

  while (cleanedValue) {
    if (trailingCurlyMetadataPattern.test(cleanedValue)) {
      cleanedValue = cleanedValue
        .replace(trailingCurlyMetadataPattern, '')
        .trim();
      continue;
    }

    const squareMetadataMatch = cleanedValue.match(
      trailingSquareMetadataPattern
    );
    if (
      squareMetadataMatch &&
      isStructuredModelRatingMetadata(squareMetadataMatch[1])
    ) {
      cleanedValue = cleanedValue
        .replace(trailingSquareMetadataPattern, '')
        .trim();
      continue;
    }

    break;
  }

  return cleanedValue;
}

function isStructuredModelRatingMetadata(value: string): boolean {
  const ratings = value.split(',');

  return (
    ratings.length > 0 &&
    ratings.every((rating) => {
      // Matches a positive integer or decimal followed by an alphabetic unit, allowing whitespace around both parts.
      // Example: the regex matches rating text "240 Vac"; non-rating text such as "V2" does not match.
      const ratingMatch = rating.match(/^\s*(\d+(?:\.\d+)?)\s*[A-Z]+\s*$/i);
      return ratingMatch !== null && Number(ratingMatch[1]) > 0;
    })
  );
}

function normalizeBatteryModelNumber(value: string): string {
  return (
    value
      .normalize('NFKC')
      // Matches supported Unicode dash variants so they can be normalized to the literal ASCII hyphen.
      // Example: the regex matches each U+2013 dash represented in "1707000\u2013XX\u2013Y", producing the non-regex value "1707000-XX-Y".
      .replace(/[\u2010-\u2015\u2212]/g, '-')
      // Matches every run of whitespace so spacing differences do not affect otherwise literal model text.
      // Example: the regex matches spaces in "aPower X-20", producing the non-regex value "aPowerX-20".
      .replace(/\s+/g, '')
  );
}

function matchesBatteryModelTemplate(
  extracted: string,
  actual: string
): boolean {
  return (
    matchesNumericCodeTemplate(extracted, actual) ||
    matchesVariableSuffixTemplate(extracted, actual) ||
    matchesGenericVariableSuffixTemplate(extracted, actual) ||
    matchesTerminalNumericSuffixTemplate(extracted, actual) ||
    matchesGeneracPwrCellTemplate(extracted, actual)
  );
}

function matchesNumericCodeTemplate(
  extracted: string,
  actual: string
): boolean {
  if (!actual.includes('-')) {
    return false;
  }

  // Matches every literal hyphen so Tesla catalog and extracted values can be compared in a compact positional form.
  // Example: the regex matches both hyphens in "1707000-XX-Y", producing the non-regex value "1707000XXY".
  const compactActual = actual.replace(/-/g, '');
  const compactExtracted = extracted.replace(/-/g, '');
  // Matches an all-numeric literal prefix followed by one or more X digit slots and one or more Y letter slots.
  // Example: the regex matches template "1707000XXY"; concrete non-regex value "170700050M" matches its generated pattern.
  const numericCodeTemplate = compactActual.match(/^(\d+)([Xx]+)([Yy]+)$/);

  if (numericCodeTemplate) {
    const [, literalPrefix, digitPlaceholders, letterPlaceholders] =
      numericCodeTemplate;
    // Anchors the literal prefix, then requires exactly the declared number of digit and letter placeholder values.
    // Example: generated regex "^1707000\\d{2}[A-Z]{1}$" matches concrete non-regex value "170700050M".
    const templatePattern = new RegExp(
      `^${literalPrefix}\\d{${digitPlaceholders.length}}[A-Z]{${letterPlaceholders.length}}$`,
      'i'
    );
    return templatePattern.test(compactExtracted);
  }

  return false;
}

function matchesVariableSuffixTemplate(
  extracted: string,
  actual: string
): boolean {
  // Matches a complete catalog value ending in at least three lowercase y placeholders and captures its literal prefix.
  // Example: the regex matches template "aPowerXyyy"; concrete non-regex value "aPowerX-20" matches its generated pattern.
  const variableSuffixTemplate = actual.match(/^(.*?)(y{3,})$/);
  if (variableSuffixTemplate) {
    const [, literalPrefix, variablePlaceholders] = variableSuffixTemplate;
    // Anchors the literal prefix and permits up to one letter, digit, or hyphen for each declared y placeholder.
    // Example: generated regex for "aPowerXyyy" matches concrete non-regex values "aPowerX" and "aPowerX-20".
    const templatePattern = new RegExp(
      `^${escapeRegularExpression(literalPrefix)}[A-Z0-9-]{0,${
        variablePlaceholders.length
      }}$`,
      'i'
    );
    return templatePattern.test(extracted);
  }

  return false;
}

function matchesGenericVariableSuffixTemplate(
  extracted: string,
  actual: string
): boolean {
  // Matches a complete catalog value with a non-empty literal prefix followed by at least three lowercase x placeholders.
  // Example: the regex matches template "B09ULF-xxx"; concrete non-regex value "B09ULF-A9-" matches its generated pattern.
  const variableSuffixTemplate = actual.match(/^(.+?)(x{3,})$/);
  if (variableSuffixTemplate) {
    const [, literalPrefix, variablePlaceholders] = variableSuffixTemplate;
    // Anchors the literal prefix and permits up to one letter, digit, or hyphen for each declared x placeholder.
    // Example: generated regex for "B09ULF-xxx" matches concrete non-regex values "B09ULF-" and "B09ULF-A9-".
    const templatePattern = new RegExp(
      `^${escapeRegularExpression(literalPrefix)}[A-Z0-9-]{0,${
        variablePlaceholders.length
      }}$`,
      'i'
    );
    return templatePattern.test(extracted);
  }

  return false;
}

function matchesTerminalNumericSuffixTemplate(
  extracted: string,
  actual: string
): boolean {
  // Matches a complete catalog value with a non-empty literal prefix followed by the exact uppercase suffix -XX.
  // Example: the regex matches template "WM-48-314-6-IN-XX"; concrete non-regex value ending in "-42" matches its generated pattern.
  const numericSuffixTemplate = actual.match(/^(.+)-XX$/);
  if (numericSuffixTemplate) {
    const [, literalPrefix] = numericSuffixTemplate;
    // Anchors the escaped literal prefix and requires exactly two digits after its terminal hyphen.
    // Example: generated regex for "WM-48-314-6-IN-XX" matches concrete non-regex value "WM-48-314-6-IN-42".
    const templatePattern = new RegExp(
      `^${escapeRegularExpression(literalPrefix)}-\\d{2}$`,
      'i'
    );
    return templatePattern.test(extracted);
  }

  return false;
}

function matchesGeneracPwrCellTemplate(
  extracted: string,
  actual: string
): boolean {
  // Matches the Generac template token x, module counts M3-M6, and either the DCB or EX configuration suffix.
  // Example: the regex matches template "PWRcellxM4EX"; concrete non-regex value "PWRcellIRM4EX" matches its generated pattern.
  const generacTemplate = actual.match(/^(.+)(x)M([3-6])(DCB|EX)$/);
  if (!generacTemplate || generacTemplate[1].toLowerCase() !== 'pwrcell') {
    return false;
  }

  const [, productName, , moduleCount, configurationType] = generacTemplate;
  // Anchors the Generac product, substitutes only IR or OR for x, and preserves the captured module and configuration values.
  // Example: generated regex for "PWRcellxM6DCB" matches concrete non-regex value "PWRcellORM6DCB".
  const templatePattern = new RegExp(
    `^${escapeRegularExpression(
      productName
    )}(?:IR|OR)M${moduleCount}${configurationType}$`,
    'i'
  );

  return templatePattern.test(extracted);
}

function escapeRegularExpression(value: string): string {
  // Matches every regular-expression metacharacter so a catalog prefix is treated as literal model text.
  // Example: the regex matches "." in "Q.HOME"; escaped non-regex text "Q\\.HOME" then matches only the literal product prefix.
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function matchExtractedByRange(
  extractedValue: any,
  actualValue: any,
  splitChar: string
): boolean {
  const [min, max] = (extractedValue as string).split(splitChar).map(Number);
  const val = Number(actualValue);
  return val >= min && val <= max;
}

function compareLessThan(extracted: any, actual: any) {
  if (Number.isNaN(extracted) || Number.isNaN(actual)) {
    return false;
  }

  return Number(actual) < Number(extracted);
}

function compareGreaterThan(extracted: any, actual: any) {
  if (Number.isNaN(extracted) || Number.isNaN(actual)) {
    return false;
  }

  return Number(actual) > Number(extracted);
}

function compareLessThanEqual(extracted: any, actual: any) {
  if (Number.isNaN(extracted) || Number.isNaN(actual)) {
    return false;
  }

  return Number(actual) <= Number(extracted);
}

function compareGreaterThanEqual(extracted: any, actual: any) {
  if (Number.isNaN(extracted) || Number.isNaN(actual)) {
    return false;
  }

  return Number(actual) >= Number(extracted);
}

function matchNormalizedManufacturer(
  extractedValue: any,
  actualValue: any
): boolean {
  return (
    normalizeManufacturer(extractedValue) === normalizeManufacturer(actualValue)
  );
}

function normalizeManufacturer(value: string): string {
  if (!value) {
    return '';
  }

  var normalizedValue = value
    .normalize('NFKD') // convert to unicode
    .replace(/[\u0300-\u036f]/g, '') // remove accent marks
    .toLowerCase()
    .replace(/&/g, ' and ') // & === and
    .replace(/['`]/g, '') // don't consider apostrophes
    .replace(/[.,/\\()-]/g, ' ') // replace common punctuation with spaces
    .replace(/\s+/g, ' ') // collapses concurrent spaces into a single space
    .trim();

  const suffixRules: Array<[RegExp, string, string]> = [
    // Incorporated / Inc
    [/\b(inc|inc\.|incorporated)\b/g, 'inc', ''],

    // Corporation / Corp
    [/\b(corp|corp\.|corporation)\b/g, 'corp', ''],

    // Company / Co
    [/\b(co|co\.|company)\b/g, 'co', ''],

    // Limited / Ltd
    [/\b(ltd|ltd\.|limited)\b/g, 'ltd', ''],

    // LLC
    [/\b(llc|l\.l\.c\.|limited liability company)\b/g, 'llc', ''],

    // Limited Liability Partnership
    [/\b(llp|l\.l\.p\.|limited liability partnership)\b/g, 'llp', ''],
  ];

  for (const [rule, replacement, remove] of suffixRules) {
    normalizedValue = normalizedValue.replace(rule, remove);
  }

  return normalizedValue.trim();
}

function isExtractedModelInActual(extracted: any, actual: any): boolean {
  const extractedNormalized = String(extracted).replace(/[XY]/g, '');
  const actualNormalized = String(actual).replace(/[XY]/g, '');

  return (
    actualNormalized
      .toLowerCase()
      .includes(extractedNormalized.toLowerCase()) ||
    extractedNormalized.toLowerCase().includes(actualNormalized.toLowerCase())
  );
}

function normalizeAddress(address: any) {
  // Replace Il and MD state code with state name
  var normalizedAddress = address
    .toLowerCase()
    .replace(/\b(il)\b/g, 'Illinois')
    .replace(/\b(md)\b/g, 'Maryland');

  // Replace abbreviated cardinal directions
  normalizedAddress = normalizedAddress
    .replace(/\bN\b/gi, 'North')
    .replace(/\bS\b/gi, 'South')
    .replace(/\bE\b/gi, 'East')
    .replace(/\bW\b/gi, 'West')
    .replace(/\bNW\b/gi, 'North West')
    .replace(/\bSW\b/gi, 'South West')
    .replace(/\bNE\b/gi, 'North East')
    .replace(/\bSE\b/gi, 'South East');

  return addressNormalize(normalizedAddress);
}

function matchNormalizedAddressComEd(extracted: any, actual: any) {
  return (
    normalizeAddress(extracted).toLowerCase() ===
    normalizeAddress(actual).toLowerCase()
  );
}
